import { useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import AppActionMenu from '../../../components/AppActionMenu'
import AppDropdown from '../../../components/AppDropdown'
import AppPagination from '../../../components/AppPagination'
import SharedFilters from '../../../components/SharedFilters'
import DateModifiedIcon from '../../../components/DateModifiedIcon'
import { useProjectListScreen } from './hooks/useProjectListScreen'
import { useProjectPermissions } from './hooks/useProjectPermissions'

function getCurrencySymbol(currency) {
  try {
    return (0).toLocaleString('en-US', {
      style: 'currency',
      currency: currency,
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).replace(/\d/g, '').trim()
  } catch {
    return currency + ' '
  }
}

function formatDisplayMoney(value, currency = 'USD') {
  const amount = Number(value || 0)
  const absolute = Math.abs(amount)
  const symbol = getCurrencySymbol(currency)
  const sign = amount < 0 ? '-' : ''
  if (absolute >= 1000000) return `${sign}${symbol}${(absolute / 1000000).toFixed(2)}M`
  if (absolute >= 1000) return `${sign}${symbol}${(absolute / 1000).toFixed(2)}k`
  return Number(amount).toLocaleString('en-US', {
    style: 'currency',
    currency: currency,
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })
}

function formatCompactCurrency(value, currency = 'USD') {
  return Number(value || 0).toLocaleString('en-US', {
    style: 'currency',
    currency: currency,
    maximumFractionDigits: 0,
  })
}

function statusDotClass(status) {
  if (status === 'IN_PROGRESS') return 'bg-emerald-500'
  if (status === 'COMPLETED') return 'bg-primary'
  return 'bg-outline'
}

function codeBadgeClass(status) {
  if (status === 'IN_PROGRESS') return 'bg-primary-fixed text-primary'
  return 'bg-surface-container text-on-surface-variant'
}

export default function ProjectInventoryListScreen() {
  const { state, actions } = useProjectListScreen()
  const { canExportProjects, canCreateProject } = useProjectPermissions()
  const navigate = useNavigate()

  const tools = useMemo(() => {
    const list = []
    if (canExportProjects) {
      list.push({ key: 'export_excel', label: 'Download Projects (Excel)', icon: 'file_download' })
    }
    if (canCreateProject) {
      list.push({ key: 'bulk_upload', label: 'Bulk Upload', icon: 'upload_file' })
    }
    return list
  }, [canExportProjects, canCreateProject])

  return (
    <div className="mx-auto w-full max-w-[1320px] pb-8">
      <header className="mb-10">
        <div className="mb-8 flex flex-col gap-6 xl:flex-row xl:items-end xl:justify-between">
          <div>
            <p className="mb-2 text-xs font-bold uppercase tracking-[0.16em] text-primary">Enterprise Portfolio</p>
            <h1 className="text-4xl font-extrabold tracking-tight text-on-surface md:text-5xl">Portfolio Ledger</h1>
          </div>
        </div>

        <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
          <article className="group relative overflow-hidden rounded-2xl bg-surface-container-lowest p-6">
            <div className="flex h-full flex-col justify-between">
              <div>
                <p className="text-xs font-bold uppercase tracking-[0.13em] text-on-surface-variant">Active Projects</p>
                <p className="mt-2 text-4xl font-extrabold tracking-tight text-on-surface">{state.stats.activeProjects}</p>
              </div>
              <div className="mt-4">
                <div className={`flex items-center gap-2 text-sm font-bold ${state.stats.trendPct > 0
                    ? 'text-emerald-600'
                    : state.stats.trendPct < 0
                      ? 'text-error'
                      : 'text-on-surface-variant'
                  }`}>
                  <span className="material-symbols-outlined text-sm">
                    {state.stats.trendPct > 0
                      ? 'trending_up'
                      : state.stats.trendPct < 0
                        ? 'trending_down'
                        : 'trending_flat'}
                  </span>
                  {(state.stats.trendPct > 0 ? '+' : '') + state.stats.trendPct}% vs last quarter
                </div>
                <p className="mt-2 text-[10px] font-bold uppercase tracking-[0.11em] text-on-surface-variant">
                  Total billable: {state.stats.activeBillableProjects}
                </p>
              </div>
            </div>
            <span className="material-symbols-outlined pointer-events-none absolute -bottom-5 -right-5 text-8xl text-surface-container opacity-20 transition-transform group-hover:scale-110">
              folder_open
            </span>
          </article>

          <article className="group relative overflow-hidden rounded-2xl bg-surface-container-lowest p-6">
            <div className="flex h-full flex-col justify-between">
              <div>
                <p className="text-xs font-bold uppercase tracking-[0.13em] text-on-surface-variant">Total Billing</p>
                <p className="mt-2 text-4xl font-extrabold tracking-tight text-on-surface">{formatDisplayMoney(state.stats.totalReceivedAmount, state.stats.currency)}</p>
              </div>
              <div className="mt-4">
                <div className="h-2 w-full rounded-full bg-surface-variant">
                  <div className="h-full rounded-full bg-primary" style={{ width: `${state.stats.billingFillPct}%` }} />
                </div>
                <p className="mt-2 text-[10px] font-bold uppercase tracking-[0.11em] text-on-surface-variant">
                  Total billable: {formatCompactCurrency(state.stats.totalBillableAmount, state.stats.currency)}
                </p>
              </div>
            </div>
            <span className="material-symbols-outlined pointer-events-none absolute -bottom-5 -right-5 text-8xl text-surface-container opacity-20 transition-transform group-hover:scale-110">
              payments
            </span>
          </article>
        </div>
      </header>

      <div className="mb-6">
        <SharedFilters
          searchField={state.searchField}
          setSearchField={actions.setSearchField}
          searchFieldOptions={state.searchFieldOptions}
          searchText={state.searchText}
          setSearchText={actions.setSearchText}
          statusFilter={state.statusFilter}
          setStatusFilter={actions.setStatusFilter}
          statusOptions={state.statusOptions}
          portfolioFilter={state.portfolioFilter}
          setPortfolioFilter={actions.setPortfolioFilter}
          portfolioOptions={state.portfolioOptions}
          projectTypeFilter={state.projectTypeFilter}
          setProjectTypeFilter={actions.setProjectTypeFilter}
          projectTypeOptions={state.projectTypeOptions}
          companyFilter={state.companyFilter}
          setCompanyFilter={actions.setCompanyFilter}
          companyOptions={state.companyOptions}
          branchFilter={state.branchFilter}
          setBranchFilter={actions.setBranchFilter}
          branchOptions={state.branchOptions}
          departmentFilter={state.departmentFilter}
          setDepartmentFilter={actions.setDepartmentFilter}
          departmentOptions={state.departmentOptions}
          leadFilter={state.leadFilter}
          setLeadFilter={actions.setLeadFilter}
          leadOptions={state.leadOptions}
          priorityFilter={state.priorityFilter}
          setPriorityFilter={actions.setPriorityFilter}
          priorityOptions={state.priorityOptions}
          billingModeFilter={state.billingModeFilter}
          setBillingModeFilter={actions.setBillingModeFilter}
          billingModeOptions={state.billingModeOptions}
          fromDate={state.fromDate}
          setFromDate={actions.setFromDate}
          toDate={state.toDate}
          setToDate={actions.setToDate}
          onReset={actions.onReset}
          tools={tools}
          onToolSelect={(key) => {
            if (key === 'export_excel') {
              actions.exportProjectsToExcel()
              return
            }
            if (key === 'bulk_upload') {
              navigate('/nerve/project/bulk-upload', {
                state: { returnTo: `/nerve/project/list${window.location.search}` },
              })
            }
          }}
          visibleAccordionFilters={[
            'portfolio',
            'projectType',
            'company',
            'branch',
            'department',
            'lead',
            'priority',
            'billingMode',
            'fromDate',
            'toDate',
          ]}
        />
      </div>

      {state.error ? (
        <div className="mb-6 rounded-xl border border-error/30 bg-error-container px-4 py-3 text-sm text-error">{state.error}</div>
      ) : null}

      <section className="overflow-hidden rounded-2xl bg-surface-container-lowest">
        <div className="overflow-x-auto">
          <table className="w-full min-w-[1120px] border-collapse text-left">
            <thead>
              <tr className="border-b border-outline-variant/10 bg-surface-container-low">
                <th className="px-6 py-4 text-xs font-black uppercase tracking-[0.13em] text-on-surface-variant">Code</th>
                <th className="px-6 py-4 text-xs font-black uppercase tracking-[0.13em] text-on-surface-variant">Project Details</th>
                <th className="px-6 py-4 text-xs font-black uppercase tracking-[0.13em] text-on-surface-variant">Status</th>
                <th className="px-6 py-4 text-xs font-black uppercase tracking-[0.13em] text-on-surface-variant">Duration</th>
                <th className="px-6 py-4 text-xs font-black uppercase tracking-[0.13em] text-on-surface-variant">Billable</th>
                <th className="px-6 py-4 text-right text-xs font-black uppercase tracking-[0.13em] text-on-surface-variant">Metrics</th>
                <th className="px-6 py-4" />
              </tr>
            </thead>

            <tbody className="divide-y divide-surface-container">
              {state.isLoading ? (
                <tr>
                  <td colSpan={7} className="px-6 py-16 text-center text-sm text-on-surface-variant">
                    Loading projects...
                  </td>
                </tr>
              ) : state.rows.length === 0 ? (
                <tr>
                  <td colSpan={7} className="px-6 py-16 text-center text-sm text-on-surface-variant">
                    No projects found for selected filters.
                  </td>
                </tr>
              ) : (
                state.rows.map((row) => (
                  <tr
                    key={row.id}
                    className={`cursor-pointer transition-colors hover:bg-surface-container-low ${row.status === 'CLOSED' ? 'opacity-70' : ''}`}
                    onClick={() => navigate(`/nerve/project/detail?id=${row.id}`, { state: { returnTo: `/nerve/project/list${window.location.search}` } })}
                  >
                    <td className="px-6 py-5 align-top">
                      <span className={`rounded-md px-2 py-1 text-sm font-black ${codeBadgeClass(row.status)}`}>{row.projectCode}</span>
                    </td>

                    <td className="px-6 py-5 align-top">
                      <div className="flex flex-wrap items-center gap-2">
                        <p className="text-base font-bold leading-tight text-on-surface">{row.name}</p>
                        {row.parentProjectId ? (
                          <span className="inline-flex items-center gap-1 rounded bg-secondary-container/50 px-1.5 py-0.5 text-[10px] font-bold text-secondary">
                            <span className="material-symbols-outlined text-xs">account_tree</span>
                            SubProject
                          </span>
                        ) : null}
                      </div>
                      <p className="mt-1 text-xs font-medium text-on-surface-variant">Lead: {row.managerName}</p>
                    </td>

                    <td className="px-6 py-5 align-top">
                      <span className="inline-flex items-center gap-2">
                        <span className={`h-2 w-2 rounded-full ${statusDotClass(row.status)}`} />
                        <span className="text-sm font-bold text-on-surface">{row.status}</span>
                      </span>
                    </td>

                    <td className="px-6 py-5 align-top">
                      <div className="flex items-center gap-1.5">
                        <p className="text-sm font-medium text-on-surface">
                          {row.startDateText} - {row.endDateText}
                        </p>
                        <DateModifiedIcon
                          hasDateChanges={row.hasDateChanges}
                          module="PROJECT"
                          entityId={row.id}
                        />
                      </div>
                      <p className="mt-1 text-[10px] font-bold uppercase tracking-[0.08em] text-on-surface-variant">{row.durationHint}</p>
                    </td>

                    <td className="px-6 py-5 align-top">
                      <span
                        className={`material-symbols-outlined ${row.billable ? 'text-emerald-600' : 'text-on-surface-variant/30'
                          }`}
                        style={row.billable ? { fontVariationSettings: "'FILL' 1" } : undefined}
                      >
                        {row.billable ? 'check_circle' : 'cancel'}
                      </span>
                    </td>

                    <td className="px-6 py-5 align-top text-right">
                      {!row.billable || !state.canViewFinancials ? (
                        <span className="text-sm font-medium text-on-surface-variant/45">—</span>
                      ) : (state.profitabilityLoadingMap || {})[row.id] ? (
                        <div className="flex flex-col items-end gap-1.5 animate-pulse">
                          <div className="h-4 w-16 rounded bg-surface-container-high" />
                          <div className="h-4 w-20 rounded bg-surface-container-high" />
                          <div className="h-4 w-24 rounded bg-surface-container-high" />
                        </div>
                      ) : (
                        (() => {
                          const rollup = (state.profitabilityMap || {})[row.id]?.rollup || { totalRevenue: 0, totalCost: 0, profitabilityAmount: 0 }
                          const isSurplus = rollup.profitabilityAmount >= 0
                          return (
                            <>
                              <p className="text-sm font-bold text-on-surface">
                                {formatCompactCurrency(rollup.totalCost, row.currency)} <span className="text-[10px] font-bold text-on-surface-variant">COST</span>
                              </p>
                              <p className="text-sm font-bold text-primary">
                                {formatCompactCurrency(rollup.totalRevenue, row.currency)} <span className="text-[10px] font-bold text-on-surface-variant">BILL</span>
                              </p>
                              <p className={`mt-1 text-sm font-black uppercase tracking-[0.08em] ${isSurplus ? 'text-emerald-600' : 'text-error'}`}>
                                {isSurplus ? '+' : ''}
                                {formatDisplayMoney(rollup.profitabilityAmount, row.currency)} {isSurplus ? 'SURPLUS' : 'DEFICIT'}
                              </p>
                            </>
                          )
                        })()
                      )}
                    </td>

                    <td className="px-6 py-5 align-top text-right" onClick={(event) => event.stopPropagation()}>
                      <AppActionMenu
                        triggerLabel=""
                        triggerIcon="more_vert"
                        showChevron={false}
                        triggerClassName="h-8 w-8 rounded-md bg-transparent p-0 text-on-surface-variant hover:bg-surface-container-high hover:text-primary"
                        menuClassName="min-w-[188px]"
                        items={[
                          { key: 'edit_project', label: 'Edit Project', icon: 'edit' },
                          ...(!row.parentProjectId && !row.parent_project_id
                            ? [{ key: 'add_subproject', label: 'Create Subproject', icon: 'account_tree' }]
                            : []),
                          { key: 'add_task', label: 'Add Task', icon: 'post_add' },
                          {
                            key: 'toggle_close',
                            label: row.status === 'CLOSED' ? 'Open Project' : 'Close Project',
                            icon: row.status === 'CLOSED' ? 'folder_open' : 'archive',
                            disabled: state.actionBusyId === row.id || !state.canEditProject,
                          },
                        ]}
                        onSelect={(key) => {
                          if (key === 'edit_project') {
                            navigate(`/nerve/project/entry?id=${row.id}`, { state: { returnTo: `/nerve/project/list${window.location.search}` } })
                            return
                          }
                          if (key === 'add_subproject') {
                            navigate(`/nerve/project/entry?mode=subproject&parent_project_id=${row.id}`, { state: { returnTo: `/nerve/project/list${window.location.search}` } })
                            return
                          }
                          if (key === 'add_task') {
                            navigate(`/nerve/task/entry?projectId=${row.id}`)
                            return
                          }
                          if (key === 'toggle_close') {
                            actions.toggleProjectClosed(row)
                          }
                        }}
                      />
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        <div className="flex items-center justify-between bg-surface-container-low px-6 py-4">
          <p className="text-sm font-medium text-on-surface-variant">
            Showing <span className="font-bold text-on-surface">{state.rangeStart}-{state.rangeEnd}</span> of {state.totalCount} projects
          </p>

          <AppPagination
            page={state.page}
            totalPages={state.totalPages}
            onPageChange={actions.goToPage}
            onPrevious={actions.goToPreviousPage}
            onNext={actions.goToNextPage}
            canPrevious={state.page > 1}
            canNext={Boolean(state.hasMore) || state.page < state.totalPages}
          />
        </div>
      </section>
    </div>
  )
}
